<template>
  <studio-tool-shell
    title="Reservationen"
    description="Buchungen pro Tag verwalten, Auslastung prüfen und neue Reservationen für Mitglieder erfassen."
    icon="mdi-calendar-clock-outline">
    <template v-slot:actions>
      <v-btn
        v-if="userService.hasRight('reservations_createType')"
        outlined
        color="#58d388"
        class="secondary-action"
        :disabled="!studioId"
        @click="$router.push({ name: 'ReservationToolAdmin' })">
        <v-icon left>mdi-tune-variant</v-icon>
        Formulare verwalten
      </v-btn>
    </template>

    <div class="metric-grid">
      <article class="metric-card">
        <span class="metric-icon"><v-icon>mdi-calendar-check-outline</v-icon></span>
        <div>
          <span>Buchungen</span><strong>{{ reservations.length }}</strong
          ><small>{{ formattedSelectedDate }}</small>
        </div>
      </article>
      <article class="metric-card">
        <span class="metric-icon"><v-icon>mdi-seat-outline</v-icon></span>
        <div>
          <span>Belegte Slots</span><strong>{{ bookedSlotCount }} / {{ totalSlotCount }}</strong
          ><small>Aktive Reservationstypen</small>
        </div>
      </article>
      <article class="metric-card">
        <span class="metric-icon"><v-icon>mdi-chart-donut-variant</v-icon></span>
        <div>
          <span>Auslastung</span><strong>{{ utilizationRate }}%</strong><small>Über alle Typen</small>
        </div>
      </article>
    </div>

    <v-card dark class="dashboard-card" flat>
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Tagesplanung</div>
          <h2>Tagesübersicht</h2>
          <p>Datum wechseln, Buchungen prüfen und neue Termine erfassen.</p>
        </div>
        <v-btn color="#009640" depressed class="primary-action" :disabled="!studioId" @click="openCreateDialog()"
          ><v-icon left>mdi-plus</v-icon>Neue Buchung</v-btn
        >
      </div>
      <v-card-text>
        <v-row align="center" justify="center" class="date-navigator">
          <v-btn icon @click="changeDay(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-chip color="primary" large v-bind="attrs" v-on="on" class="date-chip">
                <v-icon left>mdi-calendar</v-icon>
                {{ formattedSelectedDate }}
              </v-chip>
            </template>
            <v-date-picker
              v-model="selectedDate"
              @input="
                dateMenu = false
                fetchReservations()
              "
              no-title></v-date-picker>
          </v-menu>
          <v-btn icon @click="changeDay(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
          <v-btn text class="today-action" @click="goToToday">Heute</v-btn>
        </v-row>

        <div class="mt-4">
          <div v-if="isLoading" class="text-center pa-10">
            <v-progress-circular indeterminate color="var(--v-primary)" size="64"></v-progress-circular>
            <p class="mt-4">Lade Buchungen für den {{ formattedSelectedDate }}...</p>
          </div>
          <div v-else-if="!reservations.length" class="text-center pa-10 white--text">
            <h3>Keine Buchungen</h3>
            <p>Für den {{ formattedSelectedDate }} wurden keine Buchungen gefunden.</p>
          </div>
          <v-expansion-panels v-else dark accordion>
            <v-expansion-panel v-for="group in groupedReservations" :key="group.id" class="grouped-panel">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center flex-grow-1">
                  <span class="font-weight-bold">{{ group.name }}</span>
                  <v-chip small :color="group.utilization > 75 ? 'red' : group.utilization > 50 ? 'orange' : 'primary'" class="mr-4">
                    {{ group.bookedSlots }} / {{ group.totalSlots }} Slots
                  </v-chip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list dense color="transparent">
                  <v-list-item v-if="!group.bookings.length">
                    <v-list-item-content>
                      <v-list-item-subtitle class="text-center">Keine Buchungen für diesen Typ.</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-for="item in group.bookings" :key="item._id" :class="{ 'is-cancelled-admin': item.isCancelled }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ formatTime(item.startTime) }} - {{ item.user ? `${item.user.vorname} ${item.user.nachname}` : "N/A" }}
                        <v-chip v-if="item.isCancelled" x-small color="red" class="ml-2">Storniert</v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn v-if="!item.isCancelled" icon @click="deleteItem(item)" color="red">
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <v-row class="mt-8" v-if="!isLoading && reservations && reservations.length > 0 && Object.keys(groupedReservations).length > 0">
          <v-col cols="12" md="7">
            <v-card class="stat-card">
              <v-card-title>Auslastung pro Typ</v-card-title>
              <apexchart type="bar" height="250" :options="utilizationByTypeChartOptions" :series="utilizationByTypeChartSeries"></apexchart>
            </v-card>
          </v-col>
          <v-col cols="12" md="5">
            <v-card class="stat-card">
              <v-card-title>Buchungen nach Typ</v-card-title>
              <apexchart type="donut" height="250" :options="typeDistributionChartOptions" :series="typeDistributionChartSeries"></apexchart>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-dialog v-model="createDialog" max-width="700px" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="dialog-card booking-dialog-card">
        <div class="dialog-header">
          <span class="dialog-mark"><v-icon>mdi-calendar-plus-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">Buchungsassistent</div>
            <h2>Neue Buchung anlegen</h2>
            <p>Schritt {{ step }} von 4</p>
          </div>
          <v-btn icon class="dialog-close" aria-label="Dialog schliessen" @click="closeCreateDialog"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-window v-model="step" class="booking-window">
          <v-window-item :value="1" class="dialog-step">
            <v-card-text class="dialog-body">
              <h3 class="step-headline">1. Für wen ist die Buchung?</h3>
              <user-search
                v-model="newBooking.user"
                ignoreRights
                class="mx-auto"
                label="Mitglied suchen..."
                @update="updateNewAssignedUser"></user-search>
            </v-card-text>
            <v-card-actions class="dialog-actions">
              <v-spacer></v-spacer>
              <v-btn text @click="closeCreateDialog">Abbrechen</v-btn>
              <v-btn class="save-btn" @click="step = 2" :disabled="!newBooking.user">Weiter</v-btn>
            </v-card-actions>
          </v-window-item>

          <v-window-item :value="2" class="dialog-step">
            <v-card-text class="dialog-body">
              <h3 class="step-headline">2. Was & Wann?</h3>
              <v-select
                :items="reservationTypes"
                item-text="name"
                item-value="_id"
                v-model="newBooking.reservationType"
                label="Leistung auswählen"
                filled
                dark
                @change="fetchTimeslotsForNewBooking"></v-select>
              <v-date-picker
                v-if="newBooking.reservationType"
                v-model="newBooking.date"
                full-width
                dark
                color="var(--v-primary)"
                @input="fetchTimeslotsForNewBooking"></v-date-picker>
            </v-card-text>
            <v-card-actions class="dialog-actions">
              <v-spacer></v-spacer>
              <v-btn text @click="step = 1">Zurück</v-btn>
              <v-btn class="save-btn" @click="step = 3" :disabled="!newBooking.date">Weiter</v-btn>
            </v-card-actions>
          </v-window-item>

          <v-window-item :value="3" class="dialog-step">
            <v-card-text class="dialog-body">
              <h3 class="step-headline">3. Verfügbare Zeit wählen</h3>
              <div v-if="isFetchingSlots" class="text-center pa-8">
                <v-progress-circular indeterminate color="var(--v-primary)"></v-progress-circular>
              </div>
              <div v-else-if="availableSlots.length" class="timeslot-grid">
                <v-btn
                  v-for="slot in availableSlots"
                  :key="slot.time"
                  @click="
                    newBooking.startTime = slot.time
                    step = 4
                  "
                  class="timeslot-btn">
                  {{ formatTime(slot.time) }}
                </v-btn>
              </div>
              <div v-else class="text-center pa-4">Keine freien Termine für diesen Tag gefunden.</div>
            </v-card-text>
            <v-card-actions class="dialog-actions">
              <v-spacer></v-spacer>
              <v-btn text @click="step = 2">Zurück</v-btn>
            </v-card-actions>
          </v-window-item>

          <v-window-item :value="4" class="dialog-step">
            <v-card-text class="dialog-body text-center">
              <h3 class="step-headline">4. Buchung überprüfen</h3>
              <p>
                Bitte bestätige die Buchung für <br /><strong>{{ selectedUserName }}</strong>
              </p>
              <v-chip color="var(--v-primary)" class="my-3" large> {{ selectedTypeName }} am {{ formatDateTime(newBooking.startTime) }} </v-chip>
            </v-card-text>
            <v-card-actions class="dialog-actions">
              <v-spacer></v-spacer>
              <v-btn text @click="step = 3">Zurück</v-btn>
              <v-btn class="save-btn" @click="createBooking" :loading="isSaving">Jetzt Buchen</v-btn>
            </v-card-actions>
          </v-window-item>
        </v-window>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="dialog-card confirm-dialog-card">
        <div class="dialog-header compact">
          <span class="dialog-mark danger"><v-icon>mdi-calendar-remove-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">Bestätigung</div>
            <h2>Buchung stornieren?</h2>
          </div>
        </div>
        <v-card-text class="dialog-body text-center mt-2"> Möchtest du diese Buchung wirklich stornieren? </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="closeDeleteDialog">Abbrechen</v-btn>
          <v-btn color="red" @click="deleteItemConfirm" :loading="isDeleting">Ja, stornieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </studio-tool-shell>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import moment from "moment"
import userSearch from "@/components/userSearch.vue"
import StudioToolShell from "@/views/dashboard/studio/components/StudioToolShell.vue"
export default {
  components: {
    userSearch,
    StudioToolShell,
  },
  data: () => ({
    userService,
    isLoading: true,
    isDeleting: false,
    reservations: [],
    studioId: null,
    deleteDialog: false,
    itemToDelete: null,
    dateMenu: false,
    selectedDate: moment().format("YYYY-MM-DD"),

    // Create & Delete Dialog Data
    createDialog: false,
    step: 1,
    isSaving: false,
    isFetchingSlots: false,
    reservationTypes: [],
    availableSlots: [],
    newBooking: {
      user: null,
      reservationType: null,
      date: moment().format("YYYY-MM-DD"),
      startTime: null,
    },
    selectedUserName: null,
    // --- NEW: Chart Data ---
    utilizationByTypeChartSeries: [{ data: [] }],
    utilizationChartOptions: {
      chart: { type: "radialBar", sparkline: { enabled: true } },
      plotOptions: {
        radialBar: {
          hollow: { size: "70%" },
          track: { background: "#2c2c2c" },
          dataLabels: {
            name: { show: false },
            value: {
              offsetY: 10,
              fontSize: "22px",
              color: "#FFFFFF",
              formatter: (val) => `${val}%`,
            },
          },
        },
      },
      labels: ["Auslastung"],
      colors: ["#00E396"], // Green for good utilization
    },
    // In data: () => ({ ... })

    utilizationByTypeChartOptions: {
      chart: { type: "bar", height: 250, toolbar: { show: false } },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true,
          distributed: true,
        },
      },
      theme: {
        monochrome: {
          enabled: true,
          color: "#008FFB",
          shadeIntensity: 0.65,
        },
      },
      // KORREKTUR 1: Datenbeschriftung im Balken
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          // Stellt sicher, dass das Prozentzeichen angezeigt wird
          return val + "%"
        },
        style: {
          colors: ["#ffffff"], // Weisse Schrift für besseren Kontrast auf den Balken
        },
        offsetX: -10, // Rückt den Text etwas nach links, damit er schöner im Balken steht
      },
      xaxis: {
        categories: [],
        labels: { style: { colors: "#FFFFFF" } },
      },
      yaxis: {
        labels: { style: { colors: "#FFFFFF" } },
      },
      legend: { show: false },
      // KORREKTUR 2: Tooltip-Design
      tooltip: {
        theme: "dark", // Ändert das Tooltip-Design auf einen dunklen Hintergrund mit hellem Text
        y: {
          formatter: function (val) {
            return val + "% Auslastung"
          },
        },
      },
    },
    typeDistributionChartSeries: [],
    typeDistributionChartOptions: {
      chart: { type: "donut", sparkline: { enabled: true } },
      labels: [],
      theme: {
        monochrome: {
          enabled: true,
          color: "#008FFB", // Primary color
          shadeIntensity: 0.65,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              value: {
                color: "#FFFFFF", // Setzt die Farbe für die Zahl auf Weiss
              },
              total: { show: true, color: "#FFFFFF", label: "Gesamt" },
            },
          },
        },
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    },
  }),

  computed: {
    bookedSlotCount() {
      return this.groupedReservations.reduce((total, group) => total + group.bookedSlots, 0)
    },
    totalSlotCount() {
      return this.groupedReservations.reduce((total, group) => total + group.totalSlots, 0)
    },
    utilizationRate() {
      return this.totalSlotCount ? Math.round((this.bookedSlotCount / this.totalSlotCount) * 100) : 0
    },
    formattedSelectedDate() {
      const date = moment(this.selectedDate)
      if (date.isSame(moment(), "day")) return "Heute"
      if (date.isSame(moment().add(1, "day"), "day")) return "Morgen"
      if (date.isSame(moment().subtract(1, "day"), "day")) return "Gestern"
      return date.format("dd, DD.MM.YYYY")
    },
    // In computed: {}
    groupedReservations() {
      // Beginne mit allen für heute aktiven Typen, auch wenn sie 0 Buchungen haben
      console.log("Calculating grouped reservations for date:", this.selectedDate, this.reservationTypes)
      const activeTypesToday = this.reservationTypes.filter((type) => this.calculateSlotsForType(type, this.selectedDate) > 0)
      console.log("Calculating grouped reservations for date:", this.selectedDate, this.reservationTypes)

      const grouped = {}

      // Fülle die Grunddaten für jeden aktiven Typ
      activeTypesToday.forEach((type) => {
        console.log("Processing type:", type.name, "for date:", this.selectedDate, "YOOOOOOOOOOO")
        const totalSlots = this.calculateSlotsForType(type, this.selectedDate)
        grouped[type._id] = {
          id: type._id,
          name: type.name,
          bookings: [],
          bookedSlots: 0,
          totalSlots: totalSlots,
          utilization: 0,
        }
      })

      // Füge die gebuchten Daten hinzu
      this.reservations.forEach((booking) => {
        const typeId = booking.reservationType._id
        if (grouped[typeId]) {
          grouped[typeId].bookings.push(booking)
        }
      })

      // Berechne die finalen Statistiken
      Object.values(grouped).forEach((group) => {
        group.bookedSlots = group.bookings.length
        group.utilization = group.totalSlots > 0 ? Math.round((group.bookedSlots / group.totalSlots) * 100) : 0
      })

      return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name))
    },
    // Keep your existing computed properties for the dialog
    selectedTypeName() {
      const type = this.reservationTypes.find((t) => t._id === this.newBooking.reservationType)
      return type ? type.name : ""
    },
  },

  created() {
    this.studioId = this.userService.getStudio()?._id || null
    if (this.studioId) {
      // Fetch types first, as they are needed for calculation
      this.fetchReservationTypesForDialog().then(() => {
        this.fetchReservations()
      })
    } else {
      this.isLoading = false
    }
  },

  methods: {
    // In methods: {}
    // Diese neue Methode in methods: {} einfügen/ersetzen
    calculateSlotsForType(type, date) {
      const dayOfWeek = moment(date).day()
      const studio = this.userService.getStudio()
      const vacationDays = studio?.vacationDays?.map((d) => moment.utc(d.date).format("YYYY-MM-DD")) || []
      const isVacation = vacationDays.includes(moment(date).format("YYYY-MM-DD"))

      let todaysHours = null
      if (isVacation) {
        todaysHours = type.openingTimes.holiday
      } else if (dayOfWeek === 6) {
        todaysHours = type.openingTimes.saturday
      } else if (dayOfWeek === 0) {
        todaysHours = type.openingTimes.sunday
      } else {
        todaysHours = type.openingTimes.regular
      }

      if (todaysHours && todaysHours.start && todaysHours.end && type.timeslotDuration > 0) {
        // KORREKTUR: Wir erstellen vollständige Zeitstempel, um die Differenz korrekt zu berechnen
        const startTime = moment.utc(`${date}T${todaysHours.start}`)
        const endTime = moment.utc(`${date}T${todaysHours.end}`)

        // NEU: Logik aus dem Backend, um 24h-Betrieb und über-Nacht zu behandeln
        if (endTime.isSameOrBefore(startTime)) {
          endTime.add(1, "day")
        }

        const durationInMinutes = endTime.diff(startTime, "minutes")
        if (durationInMinutes > 0) {
          return Math.floor(durationInMinutes / type.timeslotDuration)
        }
      }
      return 0 // Gib 0 zurück, wenn keine Slots berechnet werden können
    },
    async fetchReservations() {
      this.isLoading = true
      try {
        const params = {
          startDate: this.$moment(this.selectedDate).startOf("day").toISOString(),
          endDate: this.$moment(this.selectedDate).endOf("day").toISOString(),
        }
        // IMPORTANT: Ensure your backend populates the full reservationType
        const response = await api.reservationTool.getReservationsForAdmin(params)
        // Sort by start time ascending
        this.reservations = response.data.sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        this.updateCharts() // <-- NEW: Update charts after fetching
      } catch (error) {
        console.error("Fehler beim Laden der Reservierungen:", error)
        this.reservations = []
      } finally {
        this.isLoading = false
      }
    },

    updateCharts() {
      // Die Logik ist jetzt in 'groupedReservations', wir müssen die Daten nur noch für die Charts aufbereiten
      const stats = this.groupedReservations

      // 1. Daten für das Auslastungs-Balkendiagramm
      this.utilizationByTypeChartOptions = {
        ...this.utilizationByTypeChartOptions,
        xaxis: {
          categories: stats.map((s) => s.name),
        },
      }
      this.utilizationByTypeChartSeries = [
        {
          data: stats.map((s) => s.utilization),
        },
      ]

      // 2. Daten für das Buchungs-Donut-Diagramm
      const bookedTypes = stats.filter((s) => s.bookedSlots > 0)
      this.typeDistributionChartOptions = {
        ...this.typeDistributionChartOptions,
        labels: bookedTypes.map((s) => s.name),
      }
      this.typeDistributionChartSeries = bookedTypes.map((s) => s.bookedSlots)
    },

    changeDay(amount) {
      this.selectedDate = moment(this.selectedDate).add(amount, "days").format("YYYY-MM-DD")
      this.fetchReservations()
    },

    goToToday() {
      this.selectedDate = moment().format("YYYY-MM-DD")
      this.fetchReservations()
    },

    formatTime(date) {
      return this.$moment.utc(date).format("HH:mm")
    },

    openCreateDialog() {
      this.resetNewBooking()
      this.createDialog = true
    },

    closeCreateDialog() {
      this.resetNewBooking()
      this.createDialog = false
    },

    resetNewBooking() {
      this.step = 1
      this.newBooking = {
        user: null,
        reservationType: null,
        date: moment().format("YYYY-MM-DD"),
        startTime: null,
      }
      this.availableSlots = []
    },

    async updateNewAssignedUser(user) {
      this.newBooking.user = user
      let customer = await api.getUserForManager(user)
      this.selectedUserName = `${customer.vorname} ${customer.nachname}`
    },

    async fetchReservationTypesForDialog() {
      try {
        const response = await api.reservationTool.getReservationTypesForAdmin(this.studioId)
        // Ensure you get the full type object, including 'days', 'openingHours', etc.
        this.reservationTypes = response.data.filter((type) => type.active)
      } catch (error) {
        console.error("Fehler beim Laden der Typen für Dialog:", error)
      }
    },

    async fetchTimeslotsForNewBooking() {
      if (!this.newBooking.reservationType || !this.newBooking.date) return
      this.isFetchingSlots = true
      this.availableSlots = []
      try {
        const response = await api.reservationTool.getTimeslots(this.newBooking.reservationType, this.newBooking.date)
        // Nur die nicht-gebuchten Slots anzeigen
        console.log("Reservation timeslots:", response)
        this.availableSlots = response.data.timeslots.filter((slot) => !slot.isBooked)
      } catch (error) {
        console.error("Fehler beim Laden der Timeslots:", error)
      } finally {
        this.isFetchingSlots = false
      }
    },

    async createBooking() {
      this.isSaving = true
      const type = this.reservationTypes.find((t) => t._id === this.newBooking.reservationType)
      const bookingData = {
        title: type.name,
        studio: this.studioId,
        user: this.newBooking.user,
        startTime: this.newBooking.startTime,
        endTime: moment.utc(this.newBooking.startTime).add(type.timeslotDuration, "minutes").toISOString(),
        isBooked: true,
        reservationType: this.newBooking.reservationType,
      }

      try {
        await api.reservationTool.createReservation(bookingData)
        this.fetchReservations() // Haupttabelle aktualisieren
        this.$toast.success("Buchung erfolgreich erstellt!") // Erfolgsmeldung
        this.closeCreateDialog()
      } catch (error) {
        // Die Fehlermeldung vom Backend ist normalerweise in error.response.data
        // Das kann ein String sein oder ein Objekt mit einer 'message'-Eigenschaft.
        const errorMessage = error.response?.data?.message || error.response?.data || "Ein unbekannter Fehler ist aufgetreten."

        console.error("Fehler beim Erstellen der Buchung:", error.response) // Logge das ganze response-Objekt für Details
        this.$toast.error(errorMessage)
      } finally {
        // 'finally' wird immer ausgeführt, egal ob erfolgreich oder nicht.
        // Perfekt, um den Lade-Status zu beenden.
        this.isSaving = false
      }
    },

    formatTime(date) {
      return this.$moment.utc(date).format("HH:mm")
    },
    formatDateTime(date) {
      return this.$moment.utc(date).format("DD.MM.YYYY [um] HH:mm")
    },

    deleteItem(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    closeDeleteDialog() {
      this.deleteDialog = false
      this.itemToDelete = null
    },

    async deleteItemConfirm() {
      this.isDeleting = true
      try {
        await api.reservationTool.deleteReservation(this.itemToDelete._id)
        this.fetchReservations() // Liste neu laden
      } catch (error) {
        console.error("Fehler beim Löschen der Reservierung:", error)
      } finally {
        this.isDeleting = false
        this.closeDeleteDialog()
      }
    },
  },
}
</script>

<style scoped>
.is-cancelled-admin {
  opacity: 0.5;
}
.is-cancelled-admin .v-list-item__title {
  text-decoration: line-through;
}
.dashboard-card {
  background-color: var(--card-bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 16px !important;
  padding: 16px;
  margin-bottom: 24px;
}
.dashboard-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  padding-bottom: 16px;
}
.date-navigator {
  gap: 8px;
}
.date-chip {
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
}
.stat-card {
  background-color: var(--bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 12px !important;
  padding: 8px;
  height: 100%;
}
.stat-card .v-card__title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-secondary);
}
.grouped-panel {
  background-color: var(--card-bg) !important;
  border-bottom: 1px solid var(--border) !important;
}
.grouped-panel:first-child {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}
.grouped-panel:last-child {
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  border-bottom: none !important;
}
.step-headline {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
}
.timeslot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  padding-top: 8px;
  max-height: 300px;
  overflow-y: auto;
}
.timeslot-btn {
  background-color: var(--border) !important;
  color: var(--text-primary) !important;
  border-radius: 12px !important;
  font-weight: 600;
  height: 48px !important;
}
/* Das CSS aus dem letzten Vorschlag kann hier grösstenteils übernommen werden */
.admin-shell {
  --bg: #121212;
  --card-bg: #1e1e1e;
  --text-primary: #ffffff;
  --text-secondary: #b0b0b0;
  --border: #2c2c2c;
  --primary-text: #000000;

  min-height: 100vh;
  padding: 40px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}
.headline {
  font-size: clamp(1.8rem, 5vw, 2.2rem);
  font-weight: 800;
  color: var(--text-primary);
}
.new-type-btn {
  color: white !important;
  font-weight: 700;
  border-radius: 12px;
}
.filter-card {
  background-color: var(--card-bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 16px !important;
  padding: 8px;
  margin-bottom: 24px;
}
.type-table {
  background-color: var(--card-bg) !important;
  border-radius: 16px !important;
  border: 1px solid var(--border);
}
.dialog-card {
  border-radius: 20px !important;
  border: 1px solid var(--border);
}
.dialog-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
}
.dialog-actions {
  padding: 16px 24px !important;
}
</style>

<style scoped>
.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.metric-card {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  background: var(--surface);
}

.metric-icon,
.dialog-mark {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.metric-icon {
  width: 2.875rem;
  height: 2.875rem;
  border-radius: 0.875rem;
}

.metric-card > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.metric-card span:not(.metric-icon),
.metric-card small {
  color: var(--muted);
  font-size: 0.75rem;
}

.metric-card strong {
  margin: 0.125rem 0;
  color: var(--text);
  font-size: 1.375rem;
  line-height: 1.2;
}

.dashboard-card {
  margin: 0 !important;
  padding: 1.375rem !important;
  border: 1px solid var(--border) !important;
  border-radius: 1.375rem !important;
  background: var(--surface) !important;
}

.surface-header,
.dialog-header {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.dialog-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text, #f4f4f5);
  font-size: 1.25rem;
}

.surface-header p,
.dialog-header p {
  margin: 0.25rem 0 0;
  color: var(--muted, #a3a3a8);
  font-size: 0.875rem;
  line-height: 1.5;
}

.primary-action,
.secondary-action,
.today-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.secondary-action {
  color: #a6edc0 !important;
}

.date-navigator {
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.date-chip {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.18) !important;
  font-size: 1rem;
}

.grouped-panel {
  margin-top: 0.5rem;
  overflow: hidden;
  border: 1px solid var(--border) !important;
  border-radius: 1rem !important;
  background: rgba(20, 20, 21, 0.7) !important;
}

.grouped-panel::before {
  box-shadow: none !important;
}

.stat-card {
  padding: 0.75rem !important;
  border: 1px solid var(--border) !important;
  border-radius: 1rem !important;
  background: rgba(255, 255, 255, 0.025) !important;
}

.dialog-card {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 1.5rem !important;
  color: var(--text) !important;
  background: #1c1c1d !important;
  box-shadow: 0 1.75rem 5rem rgba(0, 0, 0, 0.42) !important;
  font-size: 1rem;
}

.booking-dialog-card {
  display: flex;
  height: 42rem;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
}

.confirm-dialog-card {
  display: flex;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
}

.dialog-header {
  flex: 0 0 auto;
  gap: 0.875rem;
  padding: 1.25rem 1.375rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
}

.dialog-header.compact {
  padding-bottom: 1rem;
}

.dialog-mark {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
}

.dialog-mark.danger {
  border-color: rgba(239, 83, 80, 0.28);
  color: #ff8a87;
  background: rgba(239, 83, 80, 0.12);
}

.dialog-header > div {
  min-width: 0;
}

.dialog-close {
  margin-left: auto;
  color: #ffffff !important;
}

.booking-window {
  min-height: 0;
  flex: 1 1 auto;
}

.booking-window ::v-deep .v-window__container,
.booking-window ::v-deep .v-window-item {
  height: 100%;
}

.dialog-step {
  display: flex;
  min-height: 0;
  flex-direction: column;
}

.dialog-body {
  min-height: 0;
  flex: 1 1 auto;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.dialog-actions {
  flex: 0 0 auto;
  min-height: 4.75rem;
  gap: 0.5rem;
  padding: 0.875rem 1.375rem !important;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.14);
}

.dialog-actions .v-btn {
  min-height: 2.75rem;
  border-radius: 0.8125rem;
  color: #ffffff !important;
  font-size: 0.875rem;
  text-transform: none;
}

.timeslot-grid {
  grid-template-columns: repeat(auto-fill, minmax(7rem, 1fr));
  max-height: none;
  gap: 0.625rem;
}

.timeslot-btn {
  min-height: 3rem !important;
  border: 1px solid var(--border);
  color: var(--text) !important;
  background: rgba(255, 255, 255, 0.045) !important;
}

@media (hover: hover) and (pointer: fine) {
  .metric-card:hover,
  .grouped-panel:hover {
    border-color: var(--border-strong) !important;
  }
}

@media (max-width: 59.9375rem) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .metric-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-card {
    padding: 0.875rem !important;
    border-radius: 1.125rem !important;
  }

  .surface-header {
    align-items: stretch;
    flex-direction: column;
  }

  .surface-header .primary-action {
    width: 100%;
  }

  .date-navigator {
    justify-content: space-between !important;
    padding: 0.625rem;
  }

  .date-chip {
    max-width: calc(100vw - 10rem);
    font-size: 0.875rem;
  }

  .today-action {
    width: 100%;
  }

  .booking-dialog-card,
  .confirm-dialog-card {
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .dialog-mark,
  .dialog-header p {
    display: none;
  }

  .dialog-body {
    padding: 0.875rem !important;
  }

  .dialog-actions {
    flex-wrap: wrap;
    padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom)) !important;
  }
}
</style>
